import { FlagPriority, FlagStates } from '@generated/graphql';
import OpenInNew from '@mui/icons-material/OpenInNew';
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined';
import Link from '@mui/material/Link';
import { Badge } from '@ovComponents/2-component/badge/badge';
import { Link as RouterLink } from '@ovComponents/2-component/orgLink/orgLink';
import type { Theme } from 'hephaestus/core';
import { Box, Divider, Tab, Tabs, Typography } from 'hephaestus/web';
import { Fragment, useMemo, useState } from 'react';
import {
  badgeSeverityFromPriority,
  getReconFlagPriorityBadgeSx,
  getReconFlagPriorityFilledSurface,
  severityFilterAccentColor,
} from '../utils/reconFlags.config';
import type { ReconFlagSeverity } from '../utils/reconFlags.types';
import { OverviewRow } from './overviewRow';
import {
  buildReconFlagDrawerHeader,
  buildReconFlagOverviewSections,
} from './reconFlagDrawerOverviewModel';
import { getReconFlagsDetailDrawerTokens } from './reconFlagsDetailDrawer.tokens';
import type {
  DetailTab,
  ReconFlagsDetailDrawerBodyProps,
} from './reconFlagsDetailDrawerBody.types';
import { ReconFlagsDrawerNotes } from './reconFlagsDrawerNotes';
import { ReconFlagsDrawerNotesHistory } from './reconFlagsDrawerNotesHistory';

const prioritySwatchColor = (
  theme: Theme,
  priority: FlagPriority | null | undefined,
): string => {
  const outline = String(
    theme.color.outlineVariant ?? theme.color.outline ?? '#c4c4c4',
  );
  if (priority == null || priority === FlagPriority.Unknown) {
    return outline;
  }
  return severityFilterAccentColor(theme, priority as ReconFlagSeverity);
};

export const ReconFlagsDetailDrawerBody = ({
  theme,
  flag,
  t,
  notesDraft,
  onNotesDraftChange,
  onNotesSaved,
  notesDisabled = false,
}: ReconFlagsDetailDrawerBodyProps) => {
  const [tab, setTab] = useState<DetailTab>('overview');

  const tokens = useMemo(() => getReconFlagsDetailDrawerTokens(theme), [theme]);

  const header = useMemo(() => buildReconFlagDrawerHeader(flag), [flag]);

  const overviewSections = useMemo(
    () => buildReconFlagOverviewSections(t, flag),
    [flag, t],
  );

  const priorityBadge = useMemo(() => {
    if (!flag.priority || flag.priority === FlagPriority.Unknown) {
      return null;
    }
    return (
      <Badge
        label={t(`reconFlags.severityLabel.${flag.priority}`)}
        variant='text'
        sx={
          getReconFlagPriorityBadgeSx(
            theme,
            badgeSeverityFromPriority(flag.priority),
          ) as any
        }
      />
    );
  }, [flag, theme, t]);

  const swatchColor = prioritySwatchColor(theme, flag.priority);
  const canComposeNotes = flag.state === FlagStates.Flagged;
  const typePillSurface = getReconFlagPriorityFilledSurface(
    theme,
    flag.priority,
  );

  const clientNameNode =
    header.clientLine &&
    (header.clientProfileUserId ? (
      <Link
        component={RouterLink}
        to={`/clients/${header.clientProfileUserId}`}
        target='_blank'
        rel='noopener noreferrer'
        underline='none'
        sx={
          {
            color: theme.color.onSurface,
            fontWeight: 600,
            minWidth: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            '&:hover': {
              textDecoration: 'underline',
            },
          } as any
        }
      >
        <Typography variant='titleMedium' component='span' noWrap>
          {header.clientLine}
        </Typography>
        <OpenInNew sx={{ fontSize: 16, flexShrink: 0 }} aria-hidden />
      </Link>
    ) : (
      <Typography variant='titleMedium' component='span' noWrap>
        {header.clientLine}
      </Typography>
    ));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        height: '100%',
      }}
    >
      <Box sx={[{ flexShrink: 0, pt: 2 }, tokens.blockHorizontalInset] as any}>
        <Box sx={tokens.summaryTitleRow as any}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
          >
            <Box sx={tokens.prioritySwatch(swatchColor) as any} aria-hidden />
            {clientNameNode}
          </Box>
          {priorityBadge}
        </Box>

        <Box sx={tokens.metadataRows as any}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography
              component='span'
              variant='labelSmall'
              sx={tokens.metadataLabel}
            >
              {t('reconFlags.drawer.summary.type')}
            </Typography>
            <Typography
              component='span'
              variant='bodySmall'
              sx={
                header.displayType
                  ? tokens.metadataTypePill(
                      typePillSurface.bgcolor,
                      typePillSurface.color,
                    )
                  : { ...tokens.metadataValue }
              }
            >
              {header.displayType ?? '—'}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography
              component='span'
              variant='labelSmall'
              sx={tokens.metadataLabel}
            >
              {t('reconFlags.drawer.summary.account')}
            </Typography>
            <Typography
              component='span'
              variant='bodySmall'
              sx={tokens.metadataValue}
            >
              {header.custodianRaw ?? '—'}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography
              component='span'
              variant='labelSmall'
              sx={tokens.metadataLabel}
            >
              {t('reconFlags.drawer.summary.firstSeen')}
            </Typography>
            <Typography
              component='span'
              variant='bodySmall'
              sx={tokens.metadataValue}
            >
              {header.createdAtDisplay ?? '—'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ px: 2 }}>
        <Tabs
          value={tab}
          variant='fullWidth'
          onChange={(_e, v: DetailTab) => setTab(v)}
          sx={[tokens.tabsRoot] as any}
        >
          <Tab value='overview' label={t('reconFlags.drawer.tabs.overview')} />
          <Tab value='notes' label={t('reconFlags.drawer.tabs.notes')} />
          <Tab value='raw' label={t('reconFlags.drawer.tabs.rawJson')} />
        </Tabs>
      </Box>
      {tab === 'notes' ? (
        <Box sx={tokens.notesTabPane as any}>
          <Box sx={tokens.notesTabHistory as any}>
            <Box
              sx={
                [
                  tokens.blockHorizontalInset,
                  { boxSizing: 'border-box' },
                ] as any
              }
            >
              <ReconFlagsDrawerNotesHistory
                theme={theme}
                flag={flag}
                t={t}
                canComposeNotes={canComposeNotes}
              />
            </Box>
          </Box>
          {canComposeNotes && (
            <Box sx={tokens.notesTabCompose as any}>
              <Box
                sx={
                  [
                    tokens.blockHorizontalInset,
                    { boxSizing: 'border-box' },
                  ] as any
                }
              >
                <ReconFlagsDrawerNotes
                  flag={flag}
                  onSaved={onNotesSaved}
                  notesDraft={notesDraft}
                  onNotesDraftChange={onNotesDraftChange}
                  hideHeading
                  textFieldDisabled={notesDisabled}
                />
              </Box>
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={tokens.scrollRegion as any}>
          <Box
            sx={
              [
                tokens.blockHorizontalInset,
                { pt: 2, pb: 1, boxSizing: 'border-box' },
              ] as any
            }
          >
            {tab === 'overview' &&
              overviewSections.map((section, sectionIndex) => {
                const showSectionDivider = sectionIndex > 0;
                const isLastSection =
                  sectionIndex === overviewSections.length - 1;
                const sectionOuterSx = {
                  mb: isLastSection ? 2.5 : 0,
                  ...section.style,
                };

                if (section.id === 'issue-summary' && section.prose) {
                  return (
                    <Fragment key={section.id}>
                      {showSectionDivider ? <Divider sx={{ my: 2 }} /> : null}
                      <Box sx={sectionOuterSx as any}>
                        <Box sx={tokens.issueCalloutRoot as any}>
                          <Box sx={tokens.issueCalloutTitleRow as any}>
                            <ReportProblemOutlined
                              sx={tokens.issueCalloutIcon as any}
                              aria-hidden
                            />
                            <Typography
                              variant='bodyMedium'
                              sx={[
                                { fontWeight: 500 },
                                tokens.issueCalloutTitle as any,
                              ]}
                            >
                              {section.heading}
                            </Typography>
                          </Box>
                          <Typography
                            variant='bodySmall'
                            sx={tokens.issueCalloutBody as any}
                          >
                            {section.prose}
                          </Typography>
                        </Box>
                      </Box>
                    </Fragment>
                  );
                }

                return (
                  <Fragment key={section.id}>
                    {showSectionDivider ? <Divider sx={{ my: 2 }} /> : null}
                    <Box sx={sectionOuterSx as any}>
                      <Typography
                        variant='titleMedium'
                        sx={tokens.sectionHeading as any}
                      >
                        {section.heading}
                      </Typography>
                      {section.prose != null &&
                        section.prose !== '' &&
                        section.id !== 'issue-summary' && (
                          <Typography
                            variant='bodySmall'
                            component='div'
                            sx={{ mb: 1, whiteSpace: 'pre-wrap' }}
                          >
                            {section.prose}
                          </Typography>
                        )}
                      {section.rows.map((row) => (
                        <OverviewRow
                          key={`${section.id}|${row.label}|${row.value}`}
                          theme={theme}
                          tokens={tokens}
                          row={row}
                        />
                      ))}
                    </Box>
                  </Fragment>
                );
              })}

            {tab === 'raw' && (
              <Box component='pre' sx={tokens.rawJsonPre as any}>
                {JSON.stringify(flag.details ?? {}, null, 2)}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
